import { TypeAnnouncementSkeleton } from '@fm-nx/contentful-types';
import { getClient } from './client';

export type ActiveAnnouncement = {
    id: string;
    message: string;
    linkText?: string;
    linkHref?: string;
};

type GetActiveAnnouncementProps = {
    preview?: boolean;
};

/**
 * Returns the most recently published `announcement` entry, or null if none.
 *
 * There is no "active" field: the Delivery API only returns published entries,
 * so publishing / unpublishing (or a Contentful Scheduled Action) is what opens
 * and closes the banner. In preview mode the Preview API returns drafts too, so
 * an upcoming announcement can be previewed before it goes live.
 *
 * The `announcement` content type is created by
 * migrations/22-create-announcement-content-type.ts; its generated type lives in
 * `@fm-nx/contentful-types` (regenerate with `npm run types` after any migration).
 */
export const getActiveAnnouncement = async ({
    preview = false,
}: GetActiveAnnouncementProps = {}): Promise<ActiveAnnouncement | null> => {
    try {
        const response = await getClient(preview).withoutUnresolvableLinks.getEntries<TypeAnnouncementSkeleton>({
            content_type: 'announcement',
            // Newest entry first (by creation), so the most recently *created*
            // announcement wins — re-editing an older one won't bump it to the top.
            order: ['-sys.createdAt'],
            limit: 1,
            include: 1,
        });

        const entry = response.items[0];
        if (!entry) {
            return null;
        }

        const { message, linkText, linkHref } = entry.fields;

        if (!message) {
            return null;
        }

        return {
            id: entry.sys.id,
            message,
            linkText: linkText || undefined,
            linkHref: linkHref || undefined,
        };
    } catch (error) {
        // Never let a CMS hiccup — or a missing content type before the migration
        // has been run — take down every page. Just hide the banner.
        console.error('Failed to fetch announcement', error);
        return null;
    }
};
